import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type {
  CampaignUsersResponse,
  ChangeUserRoleResponse,
  ResetVolunteerPasswordResponse,
} from '@signalvote/shared';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all staff/volunteers for the currently selected campaign
 */
export function useVolunteers() {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);

  return useQuery({
    queryKey: [...queryKeys.volunteers.list(), campaignId],
    queryFn: async () => {
      const data = await apiJson<CampaignUsersResponse>(
        `/api/campaigns/${campaignId}/users?pageSize=500`,
        { error: 'Failed to fetch campaign staff' },
      );
      return data.users;
    },
    enabled: authService.isAuthenticated() && !!campaignId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Delete a volunteer
 */
export function useDeleteVolunteer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (volunteerId: string) =>
      apiVoid(`/api/admin/volunteers/${volunteerId}`, {
        method: 'DELETE',
        error: (body) => new Error(body.message || 'Failed to delete volunteer'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}

/**
 * Toggle a volunteer's platform admin status
 */
export function useUpdateVolunteerRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, isPlatformAdmin }: { userId: string; isPlatformAdmin: boolean }) =>
      apiJson<ChangeUserRoleResponse>('/api/admin/change-user-role', {
        method: 'POST',
        json: { userId, isPlatformAdmin },
        error: (body) => new Error(body.error || 'Failed to update platform admin status'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}

/**
 * Reset a volunteer's password
 */
export function useResetVolunteerPassword() {
  return useMutation({
    mutationFn: ({ volunteerId, customPassword }: { volunteerId: string; customPassword?: string | null }) =>
      apiJson<ResetVolunteerPasswordResponse>('/api/admin/reset-volunteer-password', {
        method: 'POST',
        json: { volunteerId, customPassword: customPassword || null },
        error: (body) => new Error(body.error || 'Failed to reset password'),
      }),
  });
}

/**
 * Toggle a user's active status
 */
export function useToggleUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      apiJson<unknown>(`/api/admin/toggle-user-status/${userId}`, {
        method: 'POST',
        error: (body) => new Error(body.error || 'Failed to toggle user status'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}
